import type { YouthProfile } from '@growup/accounts-shared';
import { Button, DateInput, Field, Input, Notice, todayInLisbon } from '@growup/ui';
import { useState, type ReactNode } from 'react';

import { messageOf, send } from '../api';
import { formatDay } from './labels';

/** The personal data, read-only. Only rendered when the API sent it (RN-A16). */
export function ProfileFields({ profile }: { profile: YouthProfile }) {
  const { address } = profile;
  const rows: [string, string][] = [
    ['Nome', profile.fullName],
    ['Data de nascimento', formatDay(profile.birthDate)],
    ['Email de contacto', profile.contactEmail ?? '—'],
    ['Telemóvel', profile.phone ?? '—'],
    ['Cartão de Cidadão', profile.ccNumber],
    [
      'Morada',
      `${address.street}, ${address.postalCode} ${address.locality}${address.country === 'PT' ? '' : ` (${address.country})`}`,
    ],
  ];
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-body sm:grid-cols-[11rem_minmax(0,1fr)]">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="font-sub text-label font-bold uppercase text-gu-ink-muted">{label}</dt>
          <dd className="[overflow-wrap:anywhere]">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Correcting someone's data (`members.manage`). `submit` replaces the default
 * save (completing an imported record goes elsewhere); `extra` adds fields
 * before the buttons.
 */
export function ProfileForm({
  accountId,
  profile,
  onDone,
  submit,
  submitLabel = 'Guardar',
  extra,
}: {
  accountId: string;
  profile: YouthProfile;
  onDone: (saved: boolean) => void;
  submit?: (profile: YouthProfile) => Promise<void>;
  submitLabel?: string;
  extra?: ReactNode;
}) {
  const [draft, setDraft] = useState(profile);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof YouthProfile>(key: K, value: YouthProfile[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));
  const setAddress = (key: keyof YouthProfile['address'], value: string) =>
    setDraft((d) => ({ ...d, address: { ...d.address, [key]: value } }));
  const orNull = (v: string) => (v.trim() === '' ? null : v);

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      if (submit) await submit(draft);
      else await send('PUT', `/api/members/members/${accountId}/profile`, draft);
      onDone(true);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Nome completo">
          <Input value={draft.fullName} onChange={(e) => set('fullName', e.target.value)} />
        </Field>
        <Field label="Data de nascimento">
          <DateInput
            value={draft.birthDate}
            max={todayInLisbon()}
            onChange={(v) => set('birthDate', v)}
          />
        </Field>
        <Field label="Email de contacto" hint="Vazio para um menor sem email próprio" optional>
          <Input
            type="email"
            value={draft.contactEmail ?? ''}
            onChange={(e) => set('contactEmail', orNull(e.target.value))}
          />
        </Field>
        <Field label="Telemóvel" optional>
          <Input value={draft.phone ?? ''} onChange={(e) => set('phone', orNull(e.target.value))} />
        </Field>
        <Field label="N.º do Cartão de Cidadão">
          <Input value={draft.ccNumber} onChange={(e) => set('ccNumber', e.target.value)} />
        </Field>
        <Field label="Rua">
          <Input
            value={draft.address.street}
            onChange={(e) => setAddress('street', e.target.value)}
          />
        </Field>
        <Field label="Código postal">
          <Input
            value={draft.address.postalCode}
            onChange={(e) => setAddress('postalCode', e.target.value)}
          />
        </Field>
        <Field label="Localidade">
          <Input
            value={draft.address.locality}
            onChange={(e) => setAddress('locality', e.target.value)}
          />
        </Field>
        <Field label="País" hint="Código de 2 letras">
          <Input
            value={draft.address.country}
            maxLength={2}
            onChange={(e) => setAddress('country', e.target.value.toUpperCase())}
          />
        </Field>
      </div>
      {extra}
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <div className="flex gap-3">
        <Button type="submit" size="sm" disabled={saving}>
          {submitLabel}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDone(false)}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}
